import React, {FunctionComponent, useState} from "react";
import {ChallengeRating} from "/src/domain/challenges/ChallengeUserRatingData";
import makeStyles from "@mui/styles/makeStyles";
import {theme} from "/src/assets/theme";
import {Chip, Collapse, Grid, Link, Tooltip} from "@mui/material";
import ChallengeButton from "/src/ui/challengeProtocol/ChallengeButton";
import {ParticipantArrow} from "/src/ui/shared/challengeProtocol/Arrow";
import dayjs from "dayjs";
import cs from "classnames";
import {useTranslation} from "react-i18next";
import {Group} from "@mui/icons-material";
import {links} from "/src/domain/shared/links";

export type ChallengeRatingViewProps = {
    rating: ChallengeRating;
}

export const ChallengeRatingView: FunctionComponent<ChallengeRatingViewProps> = ({rating}) => {
    const [isExpanded, setExpanded] = useState(false);
    const classes = useStyles();
    const {t} = useTranslation();

    const protocolLink = `${links.challengesOrigin}/${rating.team_id ? 'protocol-team' : 'protocol'}?id=${rating.challengeKey}`
    
    return (
        <div onClick={() => {
            setExpanded(!isExpanded)
        }} className={classes.container}>
            <Grid
                container
                component={ChallengeButton}
                className={classes.container}
                alignItems="center"
            >
                <Grid
                    item container xs={11} spacing={1} alignItems="center" className={classes.content}>
                    <Grid item xs={2} justifyContent="center">
                        <div className={classes.challengeDay}>{dayjs(rating.run_start).format('D')}</div>
                        <div className={classes.challengeMonth}>{dayjs(rating.run_start).format('MMM')}</div>
                    </Grid>
                    <Grid item xs={1}></Grid>
                    <Grid item xs={6}>
                        <Link href={protocolLink} underline="hover" onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                            {rating.challengeName}
                        </Link>
                        {!Boolean(rating.teamName) && Boolean(rating.distanceName) &&
                            <div className={classes.distance}>{rating.distanceName}</div>}
                        {Boolean(rating.teamName) &&
                            <div className={classes.team}><Group color="secondary"/><span
                                className={classes.teamName}>{rating.teamName}</span></div>}
                    </Grid>
                    <Grid item container xs={2} justifyContent="flex-end">
                       <span className={cs({
                           [classes.negative]: rating.deltarating < 0
                       })}>{rating.rating}</span>
                    </Grid>
                    <Grid item container xs={1}>
                        <ParticipantArrow
                            rotate={isExpanded}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Collapse in={isExpanded}>
                <div className={classes.detailsContainer}>
                    <CustomChip className={cs(classes.chip, classes.green)} label={`M0: ${rating.place100}`} tooltip={ t('ratedPlace') } />
                    <CustomChip className={cs(classes.chip, classes.green)} label={`M1: ${rating.placeexp}`} tooltip={ t('mathematicalExpectationPlace') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B1: ${rating.expp}`} tooltip={ t('experiencePoints') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B2: ${rating.progress_points}`} tooltip={ t('progressPoints') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B3: ${rating.absp}`} tooltip={ t('absolutePoints') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B4: ${rating.bonusprize}`} tooltip={ t('bonusPrize') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B6: ${rating.b6}`} tooltip={ t('personalRecordBonus') } />
                    <CustomChip className={cs(classes.chip, classes.blue)} label={`B7: ${rating.b7}`} tooltip={ t('platformAward') } />
                    <CustomChip className={cs(classes.chip, classes.red)} label={`S1: ${rating.deltarating}`} tooltip={ t('sumOfPoints') } />
                </div>
                <Link className={classes.link} href={`${links.origin}/math_of_rating`}>{t('math_of_rating')}</Link>
            </Collapse>
        </div>
    )
}

interface CustomChipProps {
    className: string;
    label: string;
    tooltip: string;
}

const CustomChip: FunctionComponent<CustomChipProps> = ({
    className,
    label,
    tooltip
}) => (
    <Tooltip arrow title={tooltip}>
        <Chip className={className} label={label} />
    </Tooltip>
)

const useStyles = makeStyles({
    container: {
        color: theme.primaryColor,
        '&:nth-child(even)': {
            backgroundColor: theme.lightPrimaryColor
        }
    },
    challengeDay: {
        fontSize: "1.5rem",
        textAlign: "center"
    },
    content: {
        padding: "0.75rem 0"
    },
    detailsContainer: {
        padding: "1rem"
    },
    chip: {
        marginRight: "0.5rem",
        marginBottom: "0.5rem"
    },
    blue: {
        background: '#D3F7FF',
        color: theme.primaryColor
    },
    green: {
        background: '#E7F9D0',
        color: theme.greenColor
    },
    red: {
        background: theme.lightSecondaryColor,
        color: theme.secondaryColor
    },
    challengeMonth: {
        textAlign: "center"
    },
    link: {
        marginLeft: "1.5rem",
        marginBottom: "1.5rem",
        display: "block",
        color: theme.primaryColor70,
        opacity: 0.7
    },
    negative: {
        color: theme.secondaryColor
    },
    distance: {
        color: theme.primaryColor70,
        opacity: 0.7
    },
    team: {
        display: "flex",
        alignItems: "flex-end"
    },
    teamName: {
        marginLeft: "0.5rem",
        display: "block",
        opacity: 0.7
    }
});
